import { useState, useEffect } from 'react';

interface Reception {
  id: string;
  number: string;
  purchaseOrderNumber: string;
  supplier: string;
  items: Array<{
    id: string;
    name: string;
    orderedQuantity: number;
    receivedQuantity: number;
    unitPrice: number;
  }>;
}

interface InvoiceFormProps {
  invoice?: any;
  onClose: () => void;
}

const mockReceptions: Reception[] = [
  {
    id: '1',
    number: 'BR-2024-001',
    purchaseOrderNumber: 'BC-2024-001',
    supplier: 'SOGUIPAH',
    items: [
      { id: '1', name: 'Paracétamol 500mg', orderedQuantity: 1000, receivedQuantity: 1000, unitPrice: 150 },
      { id: '2', name: 'Amoxicilline 250mg', orderedQuantity: 500, receivedQuantity: 500, unitPrice: 300 }
    ]
  },
  {
    id: '2',
    number: 'BR-2024-002',
    purchaseOrderNumber: 'BC-2024-002',
    supplier: 'Pharmacie Centrale',
    items: [
      { id: '3', name: 'Aspirine 100mg', orderedQuantity: 800, receivedQuantity: 800, unitPrice: 200 },
      { id: '4', name: 'Ibuprofène 400mg', orderedQuantity: 600, receivedQuantity: 600, unitPrice: 250 }
    ]
  }
];

export default function InvoiceForm({ invoice, onClose }: InvoiceFormProps) {
  const [formData, setFormData] = useState({
    supplierInvoiceNumber: '',
    supplier: '',
    date: new Date().toISOString().split('T')[0],
    currency: 'GNF',
    paymentTerms: '30',
    selectedReceptions: [] as string[],
    items: [] as Array<{
      id: string;
      name: string;
      orderedQuantity: number;
      receivedQuantity: number;
      orderedUnitPrice: number;
      invoicedUnitPrice: number;
      invoicedQuantity: number;
      priceVariance: boolean;
    }>
  });

  const [availableReceptions, setAvailableReceptions] = useState<Reception[]>(mockReceptions);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (invoice) {
      setFormData({
        supplierInvoiceNumber: invoice.supplierInvoiceNumber || '',
        supplier: invoice.supplier || '',
        date: invoice.date || new Date().toISOString().split('T')[0],
        currency: invoice.currency || 'GNF',
        paymentTerms: '30',
        selectedReceptions: invoice.receptionNumbers || [],
        items: []
      });
    }
  }, [invoice]);

  const handleReceptionSelect = (receptionId: string, checked: boolean) => {
    const reception = availableReceptions.find(r => r.id === receptionId);
    if (!reception) return;

    if (checked) {
      setFormData(prev => ({
        ...prev,
        selectedReceptions: [...prev.selectedReceptions, reception.number],
        supplier: reception.supplier,
        items: [
          ...prev.items,
          ...reception.items.map(item => ({
            id: item.id,
            name: item.name,
            orderedQuantity: item.orderedQuantity,
            receivedQuantity: item.receivedQuantity,
            orderedUnitPrice: item.unitPrice,
            invoicedUnitPrice: item.unitPrice,
            invoicedQuantity: item.receivedQuantity,
            priceVariance: false
          }))
        ]
      }));
    } else {
      const receptionItems = reception.items.map(item => item.id);
      setFormData(prev => ({
        ...prev,
        selectedReceptions: prev.selectedReceptions.filter(r => r !== reception.number),
        items: prev.items.filter(item => !receptionItems.includes(item.id))
      }));
    }
  };

  const handleItemChange = (itemId: string, field: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };
          
          // Vérifier l'écart tarifaire
          if (field === 'invoicedUnitPrice') {
            updatedItem.priceVariance = Math.abs(updatedItem.invoicedUnitPrice - updatedItem.orderedUnitPrice) > 0;
          }
          
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const calculateDueDate = () => {
    const invoiceDate = new Date(formData.date);
    const dueDate = new Date(invoiceDate);
    dueDate.setDate(dueDate.getDate() + parseInt(formData.paymentTerms));
    return dueDate.toISOString().split('T')[0];
  };

  const calculateTotal = () => {
    return formData.items.reduce((total, item) => {
      return total + (item.invoicedQuantity * item.invoicedUnitPrice);
    }, 0);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.supplierInvoiceNumber.trim()) {
      newErrors.supplierInvoiceNumber = 'Le numéro de facture fournisseur est requis';
    }

    if (!formData.supplier.trim()) {
      newErrors.supplier = 'Le fournisseur est requis';
    }

    if (formData.selectedReceptions.length === 0) {
      newErrors.receptions = 'Au moins une réception doit être sélectionnée';
    }

    if (formData.items.length === 0) {
      newErrors.items = 'Au moins un article doit être ajouté';
    }

    // Vérifier les quantités facturées
    formData.items.forEach((item, index) => {
      if (item.invoicedQuantity > item.receivedQuantity) {
        newErrors[`item_${index}`] = `La quantité facturée ne peut pas dépasser la quantité reçue (${item.receivedQuantity})`;
      }
      if (item.invoicedQuantity <= 0) {
        newErrors[`item_${index}`] = 'La quantité facturée doit être supérieure à 0';
      }
      if (item.invoicedUnitPrice <= 0) {
        newErrors[`price_${index}`] = 'Le prix unitaire doit être supérieur à 0';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const invoiceData = {
      ...formData,
      dueDate: calculateDueDate(),
      totalAmount: calculateTotal(),
      status: 'En Attente',
      paidAmount: 0
    };

    console.log('Facture enregistrée:', invoiceData);
    
    // Vérifier les écarts tarifaires
    const priceVariances = formData.items.filter(item => item.priceVariance);
    if (priceVariances.length > 0) {
      alert(`Attention: Des écarts tarifaires ont été détectés sur ${priceVariances.length} article(s). Veuillez vérifier les prix.`);
    }

    alert('Facture enregistrée avec succès!');
    onClose();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' ' + formData.currency;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {invoice ? 'Modifier la Facture' : 'Nouvelle Facture Fournisseur'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Informations générales */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Numéro Facture Fournisseur *
            </label>
            <input
              type="text"
              value={formData.supplierInvoiceNumber}
              onChange={(e) => setFormData(prev => ({ ...prev, supplierInvoiceNumber: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                errors.supplierInvoiceNumber ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Ex: SUP-INV-2024-001"
            />
            {errors.supplierInvoiceNumber && (
              <p className="mt-1 text-sm text-red-600">{errors.supplierInvoiceNumber}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de Facture *
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Devise
            </label>
            <select
              value={formData.currency}
              onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="GNF">GNF - Franc Guinéen</option>
              <option value="USD">USD - Dollar Américain</option>
              <option value="EUR">EUR - Euro</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Conditions de Paiement
            </label>
            <select
              value={formData.paymentTerms}
              onChange={(e) => setFormData(prev => ({ ...prev, paymentTerms: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="0">Comptant</option>
              <option value="15">15 jours</option>
              <option value="30">30 jours</option>
              <option value="45">45 jours</option>
              <option value="60">60 jours</option>
            </select>
          </div>
        </div>

        {/* Date d'échéance calculée */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center">
            <i className="ri-calendar-check-line text-blue-600 mr-2"></i>
            <span className="text-sm font-medium text-blue-800">
              Date d'échéance calculée: {new Date(calculateDueDate()).toLocaleDateString('fr-FR')}
            </span>
          </div>
        </div>

        {/* Sélection des réceptions */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-4">
            Rapprochement avec les Réceptions *
          </label>
          {errors.receptions && (
            <p className="mb-2 text-sm text-red-600">{errors.receptions}</p>
          )}
          <div className="space-y-3">
            {availableReceptions.map((reception) => (
              <div key={reception.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    id={`reception-${reception.id}`}
                    checked={formData.selectedReceptions.includes(reception.number)}
                    onChange={(e) => handleReceptionSelect(reception.id, e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor={`reception-${reception.id}`} className="ml-3 flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {reception.number} - BC: {reception.purchaseOrderNumber}
                    </div>
                    <div className="text-sm text-gray-500">
                      Fournisseur: {reception.supplier}
                    </div>
                  </label>
                </div>
                
                {formData.selectedReceptions.includes(reception.number) && (
                  <div className="ml-7 space-y-2">
                    {reception.items.map((item) => (
                      <div key={item.id} className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                        {item.name} - Qté reçue: {item.orderedQuantity} - Prix: {formatCurrency(item.unitPrice)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Articles de la facture */}
        {formData.items.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Articles à Facturer
            </label>
            {errors.items && (
              <p className="mb-2 text-sm text-red-600">{errors.items}</p>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Article</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qté Reçue</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qté Facturée</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix Commandé</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix Facturé</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {formData.items.map((item, index) => (
                    <tr key={item.id} className={item.priceVariance ? 'bg-orange-50' : ''}>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                        {item.priceVariance && (
                          <div className="text-xs text-orange-600 flex items-center mt-1">
                            <i className="ri-error-warning-line mr-1"></i>
                            Écart tarifaire détecté
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{item.receivedQuantity}</td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min="1"
                          max={item.receivedQuantity}
                          value={item.invoicedQuantity}
                          onChange={(e) => handleItemChange(item.id, 'invoicedQuantity', parseInt(e.target.value) || 0)}
                          className={`w-20 px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                            errors[`item_${index}`] ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                        {errors[`item_${index}`] && (
                          <p className="mt-1 text-xs text-red-600">{errors[`item_${index}`]}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(item.orderedUnitPrice)}</td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.invoicedUnitPrice}
                          onChange={(e) => handleItemChange(item.id, 'invoicedUnitPrice', parseFloat(e.target.value) || 0)}
                          className={`w-24 px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                            errors[`price_${index}`] ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                        {errors[`price_${index}`] && (
                          <p className="mt-1 text-xs text-red-600">{errors[`price_${index}`]}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {formatCurrency(item.invoicedQuantity * item.invoicedUnitPrice)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan={5} className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                      Total Facture:
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900">
                      {formatCurrency(calculateTotal())}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {invoice ? 'Modifier' : 'Enregistrer'} la Facture
          </button>
        </div>
      </form>
    </div>
  );
}